import { EMPTY_PERMISSIONS, PermissionMap } from "../models/User";

type Policy = {
  id?: string;
  name?: string;
};

// Policy names (case-insensitive) that grant facilitador capabilities.
const FACILITADOR_POLICY_NAMES = ["facilitador", "facilitator"];

/**
 * Resolves a merged list of Directus policies (from role and/or user) into
 * a flat frontend PermissionMap. Policies can be attached to a user's role
 * or directly to the user — both must be passed here already merged.
 */
export function resolvePermissions(policies: Policy[]): PermissionMap {
  const isFacilitador = policies.some((p) =>
    FACILITADOR_POLICY_NAMES.includes((p.name ?? "").toLowerCase())
  );

  return {
    ...EMPTY_PERMISSIONS,
    "shift.assign_any_interviewer": isFacilitador,
    "shift.assign_any_assistant": isFacilitador,
    "shift.remove_any_interviewer": isFacilitador,
    "shift.remove_any_assistant": isFacilitador,
  };
}

/**
 * Merges role-level and user-level policy arrays, deduplicating by policy id.
 * User-level policies are appended after role policies; dedup keeps the first
 * occurrence, so role policies take precedence on id collision.
 */
export function mergePolicies(
  rolePolicies: Policy[],
  userPolicies: Policy[]
): Policy[] {
  const seen = new Set<string>();
  const result: Policy[] = [];

  for (const policy of [...rolePolicies, ...userPolicies]) {
    const key = policy.id ?? policy.name ?? "";
    if (!seen.has(key)) {
      seen.add(key);
      result.push(policy);
    }
  }

  return result;
}

/**
 * Raw user shape returned by getUsers() — role and policies are nested objects
 * as returned by the Directus SDK with the expanded field paths.
 *
 * role       → { name: string }                    (directus_roles)
 * policies   → [{ policy: { name: string } }]      (junction via directus_access)
 */
type RawUserWithRoleInfo = {
  role?: { name?: string } | null;
  policies?: { policy?: { name?: string } | null }[];
};

/**
 * Returns true if the user's role name OR any of their directly-assigned
 * policies matches one of the given capability names (case-insensitive).
 */
export function userHasCapability(
  user: RawUserWithRoleInfo,
  capabilityNames: string[]
): boolean {
  const roleName = (user.role?.name ?? "").toLowerCase();
  if (capabilityNames.includes(roleName)) return true;

  const directPolicies = user.policies ?? [];
  return directPolicies.some((junction) => {
    const policyName = (junction.policy?.name ?? "").toLowerCase();
    return capabilityNames.includes(policyName);
  });
}

// Capability name sets used for filtering assignable users.
export const INTERVIEWER_CAPABILITY_NAMES = ["entrevistador", "interviewer"];

// Assistants can be filled by both interviewers and assistants.
export const ASSISTANT_CAPABILITY_NAMES = [
  "entrevistador", "interviewer",
  "assistente", "assistant", "auxiliar",
];
